type Callback<T> = () => Promise<T>;

export type AsyncQueue<T = void> = {
  push: (task: Callback<T>) => Promise<T>;
  flush: () => Promise<void>;
  size: number;
};

export function createAsyncQueue<T = void>(opts = { dedupeConcurrent: false }): AsyncQueue<T> {
  const { dedupeConcurrent } = opts;
  let queue: Callback<T>[] = [];
  let running: Promise<void> | undefined;
  let nextPromise = new DeferredPromise<T>();
  const push = (task: Callback<T>) => {
    let taskPromise = new DeferredPromise<T>();
    if (dedupeConcurrent) {
      queue = [];
      if (nextPromise.started) nextPromise = new DeferredPromise<T>();
      taskPromise = nextPromise;
    }
    queue.push(() => {
      taskPromise.started = true;
      task()
        .then((x) => taskPromise.resolve(x))
        .catch(taskPromise.reject);
      return taskPromise.promise;
    });
    if (!running) running = start();
    return taskPromise.promise;
  };
  const start = async () => {
    while (queue.length) {
      const task = queue.shift()!;
      await task().catch(() => {
        // do nothing
      });
    }
    running = undefined;
  };
  return {
    push,
    flush: () => running || Promise.resolve(),
    get size() {
      return queue.length;
    },
  };
}

export const createAsyncQueues = <T = void>(opts = { dedupeConcurrent: false }) => {
  const queues: { [queueId: string]: AsyncQueue<T> } = {};
  const push = (queueId: string, task: Callback<T>) => {
    if (!queues[queueId]) queues[queueId] = createAsyncQueue<T>(opts);
    return queues[queueId].push(task);
  };
  const flush = (queueId: string) => {
    if (!queues[queueId]) queues[queueId] = createAsyncQueue<T>(opts);
    return queues[queueId].flush();
  };
  return { push, flush };
};

class DeferredPromise<T = void, E = any> {
  started = false;
  resolve: (x: T | PromiseLike<T>) => void = () => {
    // do nothing
  };
  reject: (x: E) => void = () => {
    // do nothing
  };
  promise: Promise<T>;

  constructor() {
    this.promise = new Promise<T>((res, rej) => {
      this.resolve = res;
      this.reject = rej;
    });
  }
}
